import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSession } from '../lib/store';
import { useAuthBootstrap } from '../lib/sessionBootstrap';
import { AppShellSkeleton } from './AppShellSkeleton';

/**
 * Authentication gate for the desktop and mobile shells.
 *
 * What it renders:
 *   - status === 'loading'        → <AppShellSkeleton/> (no flash of gated UI)
 *   - status === 'unauthenticated'→ <Navigate to="/sign-in?next=..."/>
 *   - status === 'authenticated'  → children
 *
 * Shell: (desktop) + (mobile). The (kiosk) shell renders ANONYMOUSLY and MUST
 * NOT be wrapped in this gate — CLAUDE.md three-shell guardrail.
 *
 * Non-obvious contracts:
 *   - Calls `useAuthBootstrap()` itself rather than expecting the caller to.
 *     Bootstrap runs once per RequireAuth tree; it is safe to mount this
 *     wrapper at the layout level (DesktopLayout / MobileLayout).
 *   - The `next` query param round-trips through SignInPage so a deep-link
 *     into a protected route survives the sign-in flow.
 */
export interface RequireAuthProps {
  children: ReactNode;
}

export function RequireAuth({ children }: RequireAuthProps) {
  useAuthBootstrap();
  const status = useSession((s) => s.status);
  const location = useLocation();

  if (status === 'loading') {
    return <AppShellSkeleton />;
  }

  if (status === 'unauthenticated') {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/sign-in?next=${next}`} replace />;
  }

  return <>{children}</>;
}
